import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import instagramService, { InstagramApiError } from '../services/instagram.service';
import { useAppFeatures } from './AppFeaturesContext';
import { useAuth } from './AuthContext';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/safeStorage';
import type { InstagramInstanceOut } from '../types/instagram.types';

// InstagramContext — список Instagram-каналов организации + выбранный инстанс.
// Паттерн TelegramContext: грузим listInstances при наличии фичи, выбор персистим.
// Приоритет выбора: предыдущий выбранный (если жив) → сохранённый в localStorage → первый.

const STORAGE_KEY = 'instagram_selected_instance_id';

interface InstagramContextType {
  instances: InstagramInstanceOut[];
  currentInstance: InstagramInstanceOut | null;
  isLoading: boolean;
  error: string | null;
  setCurrentInstance: (instance: InstagramInstanceOut | null) => void;
  refreshInstances: () => Promise<void>;
}

const Ctx = createContext<InstagramContextType | null>(null);

export const InstagramProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const features = useAppFeatures();
  const { isAuthenticated } = useAuth();
  const [instances, setInstances] = useState<InstagramInstanceOut[]>([]);
  const [currentInstance, setCurrentInstanceState] = useState<InstagramInstanceOut | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshInstances = useCallback(async () => {
    if (!isAuthenticated || !features.instagram) {
      setInstances([]);
      setCurrentInstanceState(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const list = await instagramService.listInstances();
      setInstances(list);
      setCurrentInstanceState((prev) => {
        const storedId = Number(safeGetItem(STORAGE_KEY));
        return (
          list.find((i) => i.id === prev?.id) ??
          list.find((i) => i.id === storedId) ??
          list[0] ??
          null
        );
      });
    } catch (e: unknown) {
      // 403 (нет скоупа read) — не ошибка для UI: модуль просто скрыт.
      if (e instanceof InstagramApiError && e.status === 403) {
        setInstances([]);
        setCurrentInstanceState(null);
      } else {
        setError(e instanceof Error ? e.message : 'Не удалось загрузить Instagram-каналы');
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, features.instagram]);

  useEffect(() => {
    void refreshInstances();
  }, [refreshInstances]);

  const setCurrentInstance = useCallback((instance: InstagramInstanceOut | null) => {
    setCurrentInstanceState(instance);
    if (instance) {
      safeSetItem(STORAGE_KEY, String(instance.id));
    } else {
      safeRemoveItem(STORAGE_KEY);
    }
  }, []);

  const value = useMemo<InstagramContextType>(
    () => ({ instances, currentInstance, isLoading, error, setCurrentInstance, refreshInstances }),
    [instances, currentInstance, isLoading, error, setCurrentInstance, refreshInstances],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useInstagram = (): InstagramContextType => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useInstagram must be used within InstagramProvider');
  return v;
};
